import { analytics } from "MVVM/Analytics";

type GPT = typeof window.googletag;
type SlotSize = [number, number];
type SizeMapping = { viewport: [number, number]; sizes: SlotSize[] }[];

interface SlotConfig {
  adUnitPath: string;
  sizes: SlotSize[];
  sizeMapping?: SizeMapping;
  targeting?: Record<string, string>;
}

interface ManagedSlot {
  slot: googletag.events.SlotRenderEndedEvent["slot"];
  slotId: string;
  config: SlotConfig;
}

type SlotEventCallback = (event: {
  slotId: string;
  isEmpty: boolean;
  size: number[] | string;
}) => void;

class DisplayAdManager {
  private static instance: DisplayAdManager | null = null;

  private gpt: GPT | null = null;
  private initPromise: Promise<GPT> | null = null;
  private initStarted = false;
  private servicesEnabled = false;
  private slots = new Map<string, ManagedSlot>();
  private pendingOperations: Promise<void> = Promise.resolve();
  private globalListenerAdded = false;
  private slotCallbacks = new Map<string, SlotEventCallback>();

  private constructor() {}

  static getInstance(): DisplayAdManager {
    if (!DisplayAdManager.instance) {
      DisplayAdManager.instance = new DisplayAdManager();
    }
    return DisplayAdManager.instance;
  }

  async initialize(): Promise<GPT> {
    if (this.gpt) {
      return this.gpt;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitialize();
    return this.initPromise;
  }

  private async doInitialize(): Promise<GPT> {
    if (this.initStarted) {
      return this.waitForGpt();
    }

    this.initStarted = true;

    if (window.googletag?.apiReady) {
      this.gpt = window.googletag;
      this.enableServicesOnce();
      this.setupGlobalListener();
      return this.gpt;
    }

    const scriptSelector = 'script[src*="securepubads.g.doubleclick.net"]';
    if (!document.querySelector(scriptSelector)) {
      const script = document.createElement("script");
      script.src = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
      script.async = true;
      document.head.appendChild(script);
    }

    window.googletag = window.googletag || ({ cmd: [] } as unknown as GPT);

    return this.waitForGpt();
  }

  private waitForGpt(): Promise<GPT> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const timeout = 5000;

      const check = () => {
        if (window.googletag?.apiReady) {
          this.gpt = window.googletag;
          this.enableServicesOnce();
          this.setupGlobalListener();
          resolve(this.gpt);
          return;
        }

        if (Date.now() - startTime > timeout) {
          reject(new Error("GPT failed to load within timeout"));
          return;
        }

        setTimeout(check, 50);
      };

      check();
    });
  }

  private enableServicesOnce(): void {
    if (this.servicesEnabled || !this.gpt) return;
    this.servicesEnabled = true;

    this.gpt.cmd.push(() => {
      this.gpt!.pubads().enableSingleRequest();
      this.gpt!.enableServices();
    });
  }

  private setupGlobalListener(): void {
    if (this.globalListenerAdded || !this.gpt) return;
    this.globalListenerAdded = true;

    this.gpt.cmd.push(() => {
      this.gpt!.pubads().addEventListener(
        "slotRenderEnded",
        (event: googletag.events.SlotRenderEndedEvent) => {
          const slotElementId = event.slot.getSlotElementId();
          const adUnitPath = event.slot.getAdUnitPath();
          const managed = this.slots.get(slotElementId);

          analytics.track("Ad Impression", {
            slotId: slotElementId,
            adUnit: adUnitPath,
            isEmpty: event.isEmpty,
            hasAd: !event.isEmpty,
            size: event.size ?? [],
            advertiserId: event.advertiserId,
            campaignId: event.campaignId,
            lineItemId: event.lineItemId,
            creativeId: event.creativeId,
            targeting: managed?.config.targeting ?? {},
            viewer_source: managed?.config.targeting?.viewer_source,
            partner: managed?.config.targeting?.partner,
            warehouseOnly: "true",
          });

          const callback = this.slotCallbacks.get(slotElementId);
          if (callback) {
            callback({
              slotId: slotElementId,
              isEmpty: event.isEmpty,
              size: event.size ?? [],
            });
          }
        },
      );
    });
  }

  async createSlot(
    slotId: string,
    config: SlotConfig,
    onRenderEnded?: SlotEventCallback,
  ): Promise<ManagedSlot["slot"] | null> {
    const operation = this.pendingOperations.then(async () => {
      await this.initialize();

      if (this.slots.has(slotId)) {
        const existing = this.slots.get(slotId)!;
        if (onRenderEnded) {
          this.slotCallbacks.set(slotId, onRenderEnded);
        }
        return existing.slot;
      }

      return this.doCreateSlot(slotId, config, onRenderEnded);
    });

    this.pendingOperations = operation.then(() => {}).catch(() => {});

    return operation;
  }

  private doCreateSlot(
    slotId: string,
    config: SlotConfig,
    onRenderEnded?: SlotEventCallback,
  ): Promise<ManagedSlot["slot"] | null> {
    return new Promise((resolve) => {
      if (!this.gpt) {
        resolve(null);
        return;
      }

      this.gpt.cmd.push(() => {
        if (this.slots.has(slotId)) {
          const existing = this.slots.get(slotId)!;
          if (onRenderEnded) {
            this.slotCallbacks.set(slotId, onRenderEnded);
          }
          resolve(existing.slot);
          return;
        }

        const adElement = document.getElementById(slotId);
        if (!adElement) {
          resolve(null);
          return;
        }

        const slot = this.gpt!.defineSlot(
          config.adUnitPath,
          config.sizes,
          slotId,
        );

        if (!slot) {
          resolve(null);
          return;
        }

        if (config.sizeMapping && config.sizeMapping.length > 0) {
          let mappingBuilder = this.gpt!.sizeMapping();
          for (const mapping of config.sizeMapping) {
            mappingBuilder = mappingBuilder.addSize(
              mapping.viewport,
              mapping.sizes,
            );
          }
          const sizeMapping = mappingBuilder.build();
          if (sizeMapping) {
            slot.defineSizeMapping(sizeMapping);
          }
        }

        if (config.targeting) {
          for (const [key, value] of Object.entries(config.targeting)) {
            slot.setTargeting(key, value);
          }
        }

        slot.addService(this.gpt!.pubads());

        this.slots.set(slotId, { slot, slotId, config });

        if (onRenderEnded) {
          this.slotCallbacks.set(slotId, onRenderEnded);
        }

        this.gpt!.display(slotId);

        resolve(slot);
      });
    });
  }

  async refreshSlot(slotId: string): Promise<void> {
    const operation = this.pendingOperations.then(() => {
      return new Promise<void>((resolve) => {
        const managed = this.slots.get(slotId);
        if (!managed || !this.gpt) {
          resolve();
          return;
        }

        this.gpt.cmd.push(() => {
          this.gpt!.pubads().refresh([managed.slot]);
          resolve();
        });
      });
    });

    this.pendingOperations = operation.catch(() => {});

    return operation;
  }

  async destroySlot(slotId: string): Promise<void> {
    const operation = this.pendingOperations.then(() => {
      return new Promise<void>((resolve) => {
        const managed = this.slots.get(slotId);
        if (!managed || !this.gpt) {
          this.slots.delete(slotId);
          this.slotCallbacks.delete(slotId);
          resolve();
          return;
        }

        this.gpt.cmd.push(() => {
          this.gpt!.destroySlots([managed.slot]);
          this.slots.delete(slotId);
          this.slotCallbacks.delete(slotId);
          resolve();
        });
      });
    });

    this.pendingOperations = operation.catch(() => {});

    return operation;
  }

  hasSlot(slotId: string): boolean {
    return this.slots.has(slotId);
  }

  isReady(): boolean {
    return this.gpt !== null && this.servicesEnabled;
  }
}

export const displayAdManager = DisplayAdManager.getInstance();
export type { SlotConfig, SlotEventCallback, SizeMapping, SlotSize };
